import { useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Sparkle } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'

import { selectUserRole } from '../../reducers/userDataReducer'
import { selectActiveBuilding } from '../../reducers/buildingSelectorReducer'

import ImproveWithAIWindow from './ImproveWithAIWindow'

const WINDOW_OFFSET_Y = 10
const MIN_AI_MESSAGE_LENGTH = 20

const ImproveWithAI = ({
  textInputRef,
  lastResidentMessage,
  sendMessageAsSMS,
  userRole,
  providedText,
  activeBuilding,
  onInsertResult = () => {},
}) => {
  const { t } = useTranslation(['improve-with-ai'])
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef(null)
  const windowRef = useRef(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const disabled = useMemo(() => {
    if (!providedText) return true
    return providedText.length < MIN_AI_MESSAGE_LENGTH
  }, [providedText])

  const updatePosition = () => {
    if (!buttonRef.current || !windowRef.current) return

    const buttonRect = buttonRef.current.getBoundingClientRect()
    const windowRect = windowRef.current.getBoundingClientRect()

    const bottomPosition = buttonRect.top + window.scrollY - WINDOW_OFFSET_Y

    setPosition({
      top: bottomPosition - windowRect.height,
      left: buttonRect.left + buttonRect.width - windowRect.width,
    })
  }

  useEffect(() => {
    if (isOpen) {
      updatePosition()

      const resizeObserver = new window.ResizeObserver(() => {
        updatePosition()
      })

      if (windowRef.current) {
        resizeObserver.observe(windowRef.current)
      }

      // Add click outside handler
      const handleClickOutside = (event) => {
        if (
          windowRef.current &&
          !windowRef.current.contains(event.target) &&
          !buttonRef.current.contains(event.target)
        ) {
          setIsOpen(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)

      return () => {
        resizeObserver.disconnect()
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  return (
    <div className="improve-with-ai--container">
      {disabled && (
        <ReactTooltip id="improve-with-ai-tooltip" place="top" multiline effect="solid">
          <span>{t('improve-with-ai:trigger.tooltip', { minLength: MIN_AI_MESSAGE_LENGTH })}</span>
        </ReactTooltip>
      )}
      <button
        ref={buttonRef}
        className="improve-with-ai--button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        data-tip={t('improve-with-ai:shared.title')}
        data-for="improve-with-ai-tooltip"
      >
        <Sparkle size={24} />
        {t('improve-with-ai:shared.title')}
      </button>
      {isOpen &&
        createPortal(
          <ImproveWithAIWindow
            ref={windowRef}
            style={{
              top: position.top,
              left: position.left,
            }}
            textInputRef={textInputRef}
            lastResidentMessage={lastResidentMessage}
            sendMessageAsSMS={sendMessageAsSMS}
            userRole={userRole}
            buildingId={activeBuilding.id}
            onClose={() => setIsOpen(false)}
            onInsertResult={onInsertResult}
          />,
          document.body,
        )}
    </div>
  )
}

ImproveWithAI.propTypes = {
  textInputRef: PropTypes.object,
  lastResidentMessage: PropTypes.object,
  sendMessageAsSMS: PropTypes.bool,
  userRole: PropTypes.string,
  providedText: PropTypes.string,
  activeBuilding: PropTypes.object,
  onInsertResult: PropTypes.func,
}

export default connect((state) => ({
  userRole: selectUserRole(state),
  activeBuilding: selectActiveBuilding(state),
}))(ImproveWithAI)
